<template>
  <div class="animated fadeIn about-page">

    <!-- Header -->
    <b-row class="mb-4">
      <b-col>
        <div class="d-flex align-items-center">
          <i class="fa fa-leaf fa-2x text-success mr-3"></i>
          <div>
            <h3 class="mb-0">Leaf Dashboard</h3>
            <small class="text-muted">Version {{ version }}</small>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row>

      <!-- Left column: dedication + info -->
      <b-col lg="4" class="mb-4">

        <!-- Dedication -->
        <b-card class="dedication-card mb-4 border-0">
          <div class="text-center mb-3">
            <img
              :src="photoSrc"
              alt="Aaron Hastings working outdoors beside a Leaf device"
              class="dedication-photo mx-auto mb-3"
            >
            <h5 class="mb-0">In Memory Of</h5>
            <h4 class="text-success font-weight-bold mt-1 mb-0">Aaron Hastings</h4>
            <small class="text-muted">abatardi</small>
          </div>
          <hr>
          <p class="dedication-text">
            This dashboard is dedicated to Aaron — a passionate engineer, a creative thinker,
            and the kind of person who made every project better just by being part of it.
            His curiosity, humor, and genuine care for the work he did live on in every line of
            code and every decision made here.
          </p>
          <p class="dedication-text mb-0">
            Gone too soon. Never forgotten.
          </p>
        </b-card>

        <!-- About blurb -->
        <b-card class="border-0 bg-light">
          <h6 class="font-weight-bold mb-2"><i class="fa fa-info-circle mr-2 text-primary"></i>About</h6>
          <p class="small text-muted mb-2">
            Leaf Dashboard is a real-time monitoring platform for water resource devices —
            wells, flumes, plunge pools, and bubblers. Built to give field teams
            instant visibility into their sensor data, alerts, and device health.
          </p>
          <p class="small text-muted mb-0">
            Built with Vue.js &amp; GraphQL &mdash;
            <a href="https://leaf.ai" target="_blank" rel="noopener">leaf.ai</a>
          </p>
        </b-card>

      </b-col>

      <!-- Right column: changelog -->
      <b-col lg="8">
        <b-card class="border-0">
          <div slot="header">
            <i class="fa fa-list-alt mr-2"></i> Changelog
          </div>

          <div v-for="(release, idx) in changelog" :key="idx" class="release mb-4">
            <div class="d-flex align-items-center mb-2">
              <span class="release-version mr-2">{{ release.version }}</span>
              <b-badge v-if="idx === 0" variant="success" class="mr-2">Latest</b-badge>
              <span class="release-date text-muted small">{{ release.date }}</span>
            </div>

            <div v-for="section in release.sections" :key="section.label" class="mb-3">
              <div class="section-label mb-1">
                <i :class="'fa ' + section.icon + ' mr-1'" :style="'color:' + section.color"></i>
                {{ section.label }}
              </div>
              <ul class="changelog-list">
                <li v-for="(item, i) in section.items" :key="i">{{ item }}</li>
              </ul>
            </div>

            <hr v-if="idx < changelog.length - 1" class="my-3">
          </div>

        </b-card>
      </b-col>

    </b-row>
  </div>
</template>

<script>
export default {
  name: 'About',
  data () {
    return {
      photoSrc: process.env.BASE_URL + 'img/aba.png',
      version: '0.5.0',
      changelog: [
        {
          version: 'v0.5.0',
          date: 'September 2026',
          sections: [
            {
              label: 'New Features',
              icon: 'fa-star',
              color: '#28a745',
              items: [
                'River device type added, with its own sensor data page showing water surface elevation and potentiometric head. Rivers reuse the well sensor, so depth is measured down from a surveyed benchmark and converted to an elevation using the device elevation.',
                'Devices can now be linked to one another from Device Configuration. Pairing a river with a well is what drives the potentiometric head chart, which plots the difference between the two water elevations — positive means the well is higher.',
                'Alert rules can now trigger automated actions on the device, not just notifications. The first action sets sleep mode, so a low battery rule can drop a device into ultra low power and a matching recovery rule can bring it back to network sleep.',
                'Device names on the Device Health page link straight through to that device configuration.',
                'Device type is now editable on an existing device. Previously only the configuration record type could be changed, which left the device itself reporting its original type.'
              ]
            },
            {
              label: 'Improvements',
              icon: 'fa-arrow-up',
              color: '#007bff',
              items: [
                'Calibration diagram corrected for flume and other direct depth sensors. The raw measurement is now drawn from the datum to the water surface, matching the firmware formula, with the sensor shown at the datum it measures from.',
                'Calibration now treats rivers the same as wells, since both derive the calibration offset from a datum rather than using a small trim.',
                'Device Configuration and Device Health now always refresh device records rather than serving a cached copy, so edits made elsewhere show up immediately.',
                'Row action menus no longer get cut off when a table is filtered down to a few rows or when the menu opens upward.',
                'The Sleep Type field warns when an alert action is controlling it, since a manual change would otherwise be silently reverted.',
                'The river page now explains why a chart is empty — no readings for the period, a missing metric, or a device elevation that has not been set — instead of rendering blank.',
                'Beta badges removed from the Bubblers and Rivers pages.'
              ]
            }
          ]
        },
        {
          version: 'v0.4.0',
          date: 'April 2026',
          sections: [
            {
              label: 'New Features',
              icon: 'fa-star',
              color: '#28a745',
              items: [
                'Audit Log viewer added under Admin settings — browse a full history of all admin actions with filters by entity type, action, and date range. Diff modal shows exactly what changed on each update.',
                'Signal strength indicator added to the device configuration table — displays the last known cell signal as a colour-coded bar graphic (green/yellow/red) with the raw value on hover.',
                'Live calibration now supports two modes: In Water (existing wizard behaviour) and Out of Water (averages raw ADC readings, subtracts 5, and saves as adcMin on the device config).',
                'Alert rules now support all available device metrics — battery, solar voltage, cell signal, case temperature, and more — in addition to primary sensor readings.',
                'Live (no aggregation) alert mode added — rules can now fire on every incoming reading without waiting for a periodic average.',
                'Admin navigation grouped into a sub-menu under Settings for a cleaner sidebar.'
              ]
            },
            {
              label: 'Improvements',
              icon: 'fa-arrow-up',
              color: '#007bff',
              items: [
                'Edit Rule redesigned as a modal — no longer scrolls to an inline card below the list.',
                'Alert rules page now shows all rules (not just the current user\'s own rules).',
                'Weather widget moved above the sensor graphs and compacted into a single-row layout.',
                'Case temperature chart shows a warning icon inside the chart when any reading is below -100°F, indicating a possible faulty BME chip.',
                'Calibration wizard sends a refreshConfig command to the device after saving, with toast notifications for success or failure.'
              ]
            }
          ]
        },
        {
          version: 'v0.3.0',
          date: 'April 2026',
          sections: [
            {
              label: 'New Features',
              icon: 'fa-star',
              color: '#28a745',
              items: [
                'Local weather widget added to all sensor dashboards — shows current conditions, 7-day forecast, and a rainfall chart matching whatever date range is selected for the graphs. Automatically switches between daily, weekly, and monthly view depending on the range. Only shown when a single device is selected. Powered by Open-Meteo with data going back to 1940.',
                'Get Depth Reading shortcut added to well devices — quickly trigger a live depth measurement from the device list.',
                'Get Readings results now display in a structured, easy-to-read layout with separate sections for Device Info, Power, and Sensor Readings instead of raw data.',
                'Command shortcuts added to every device — quickly send Get Readings, Get Battery Charge, Refresh Config, or Reset Device directly from the device list. Results are displayed clearly with parsed values.',
                'Sensor calibration workflow — select a sensor to calibrate directly from the device list. The calibration wizard now zeroes the sensor offset and refreshes the device config automatically before taking readings, ensuring accurate baseline measurements.',
                'Live Events modal — renamed and expanded from "Actions". View real-time device events, send manual commands, browse command history, and inspect device state all in one place.',
                'Device replacement flow — provision a new Particle device by ID or serial number when swapping hardware. Historical sensor data can be re-tagged to the new serial in the same operation.',
                'Team device permissions — admins can now assign, edit, and remove device access per team, with Read, Write, and Full permission levels.',
                'Admin password reset — administrators can now set passwords for any user account directly from the User Management screen.'
              ]
            },
            {
              label: 'Improvements',
              icon: 'fa-arrow-up',
              color: '#007bff',
              items: [
                'Device configuration, user management, and team management screens are now fully mobile-friendly. Long tables collapse gracefully on small screens, and action buttons are tucked into a compact menu.',
                'Sensor data pages now include a "Deselect All" option, making it much easier to focus on a single sensor without unchecking each one individually.',
                'The device sensors table in configuration is now mobile-friendly with responsive column hiding and a compact action menu.',
                'Device configuration now saves changes and immediately sends a refresh command to the device so new settings take effect without a manual restart.',
                'Improved sleep mode labeling in device configuration — Stop/Network and ULP modes are clearly described with a tooltip explaining the connectivity implications of each.'
              ]
            },
            {
              label: 'Bug Fixes',
              icon: 'fa-bug',
              color: '#dc3545',
              items: [
                'Fixed Replace Device button not enabling when entering a Particle Device ID.',
                'Fixed action dropdowns being clipped inside scrollable table containers.',
                'Fixed an icon rendering issue with the Replace button.'
              ]
            }
          ]
        },
        {
          version: 'v0.1.2',
          date: 'February 2026',
          sections: [
            {
              label: 'New Features',
              icon: 'fa-star',
              color: '#28a745',
              items: [
                'User and team management added to the admin settings area.',
                'Admins can create new user accounts, edit user details, assign roles, and manage team memberships.',
                'Device health monitoring page — see publish intervals, battery, and connectivity status across all devices at a glance.'
              ]
            },
            {
              label: 'Improvements',
              icon: 'fa-arrow-up',
              color: '#007bff',
              items: [
                'Device configuration page redesigned with tabbed settings, inline sensor management, and a live save progress indicator.',
                'Sensor edit page updated with clearer calibration offset explanation.',
                'Alert summaries now show device and rule context more prominently.'
              ]
            },
            {
              label: 'Bug Fixes',
              icon: 'fa-bug',
              color: '#dc3545',
              items: [
                'Fixed save button not responding on device configuration in certain states.',
                'Resolved an issue where alert rules could not be deleted from the rules list.'
              ]
            }
          ]
        },
        {
          version: 'v0.1.1',
          date: 'January 2026',
          sections: [
            {
              label: 'New Features',
              icon: 'fa-star',
              color: '#28a745',
              items: [
                'Sensor calibration page — use historical data or a live 5-read wizard to compute and apply a calibration offset to any sensor.',
                'Sensor edit page — update sensor label, type, unit, interface, channel, and calibration value.',
                'Bubbler device support added to the sensor data dashboard.'
              ]
            },
            {
              label: 'Improvements',
              icon: 'fa-arrow-up',
              color: '#007bff',
              items: [
                'Chart date range picker now defaults to the last 7 days.',
                'Improved loading states and error messages throughout the dashboard.',
                'Notification target test button added — send a test alert to verify delivery before activating a rule.'
              ]
            }
          ]
        },
        {
          version: 'v0.1.0',
          date: 'December 2025',
          sections: [
            {
              label: 'Initial Release',
              icon: 'fa-rocket',
              color: '#6f42c1',
              items: [
                'Real-time sensor data charts for wells, flumes, and plunge pools.',
                'Alert rules engine — define thresholds and receive notifications via SMS, email, or web.',
                'Device configuration management — create and update device configs, manage sensors.',
                'Account management with password change.',
                'Role-based access control — Admin, Customer, and Read-Only roles.',
                'Particle Cloud integration for live device commands and event streaming.'
              ]
            }
          ]
        }
      ]
    }
  }
}
</script>

<style scoped>
.about-page {
  padding-bottom: 2rem;
}

.dedication-photo {
  display: block;
  width: 100%;
  max-width: 260px;
  aspect-ratio: 3 / 4;
  object-fit: cover;
  object-position: center 18%;
  border-radius: 18px;
  box-shadow: 0 14px 30px rgba(40, 167, 69, 0.18);
  border: 4px solid rgba(255, 255, 255, 0.9);
}

.dedication-card {
  background: linear-gradient(135deg, #f8fff9, #f0fff4);
  border-left: 4px solid #28a745 !important;
}

.dedication-text {
  font-size: 0.9rem;
  color: #495057;
  line-height: 1.7;
}

.release-version {
  font-size: 1.1rem;
  font-weight: 700;
  color: #343a40;
  font-family: monospace;
}

.release-date {
  font-size: 0.8rem;
}

.section-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
  color: #6c757d;
}

.changelog-list {
  padding-left: 1.2rem;
  margin-bottom: 0;
}

.changelog-list li {
  font-size: 0.88rem;
  color: #495057;
  margin-bottom: 0.3rem;
  line-height: 1.5;
}
</style>
